import { useCallback, useEffect, useState } from "react";
import {
  api,
  ApiError,
  type ConsultationDetail,
  type ConsultationListResponse,
  type ConsultationRow,
} from "../lib/api.ts";
import {
  addDays,
  formatLongDate,
  formatShortDate,
  formatTime,
  todayKey,
} from "../lib/format.ts";
import { formatAnswer } from "../consultation/ConsultationFields.tsx";

interface ConsultationSubmissionsProps {
  token: string;
  onUnauthorised: () => void;
}

type Filter = "awaiting" | "received" | "all";

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "awaiting", label: "Not in yet" },
  { id: "received", label: "Received" },
  { id: "all", label: "Everyone" },
];

const EMAIL_LABEL: Record<string, string> = {
  confirmation: "Confirmation",
  cancellation: "Cancellation",
  nudge: "Form reminder",
  reminder: "Appointment reminder",
};

/** How long ago, in the words someone would actually use. */
function ago(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 14) return `${days} days ago`;
  return `on ${formatShortDate(iso)}`;
}

/** Days until the appointment. Negative means it has been and gone. */
function daysUntil(iso: string): number {
  const start = new Date(iso).getTime();
  return Math.ceil((start - Date.now()) / 86_400_000);
}

export function ConsultationSubmissions({
  token,
  onUnauthorised,
}: ConsultationSubmissionsProps) {
  const [horizonDays, setHorizonDays] = useState(60);
  const [filter, setFilter] = useState<Filter>("awaiting");
  const [list, setList] = useState<ConsultationListResponse | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ConsultationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [runningReminders, setRunningReminders] = useState(false);

  const from = todayKey();
  const to = addDays(from, horizonDays);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      try {
        setList(await api.admin.getConsultations(token, from, to, signal));
      } catch (caught) {
        if ((caught as Error)?.name === "AbortError") return;
        if (caught instanceof ApiError && caught.status === 401)
          return onUnauthorised();
        setError(
          caught instanceof ApiError
            ? caught.message
            : "That could not be loaded.",
        );
      }
    },
    [token, from, to, onUnauthorised],
  );

  useEffect(() => {
    const controller = new AbortController();
    setList(null);
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (openId === null) {
      setDetail(null);
      return;
    }
    const controller = new AbortController();
    setDetail(null);
    api.admin
      .getConsultation(token, openId, controller.signal)
      .then(setDetail)
      .catch((caught: unknown) => {
        if ((caught as Error)?.name === "AbortError") return;
        if (caught instanceof ApiError && caught.status === 401)
          return onUnauthorised();
        setError("Those answers could not be loaded.");
      });
    return () => controller.abort();
  }, [openId, token, onUnauthorised]);

  async function nudge(row: ConsultationRow) {
    const when = formatLongDate(row.startUtc);
    if (
      !window.confirm(
        `Email ${row.customerName} at ${row.customerEmail} asking them to fill in their form before ${when}?`,
      )
    ) {
      return;
    }

    setBusyId(row.bookingId);
    setError(null);
    try {
      const result = await api.admin.nudgeConsultation(token, row.bookingId);
      setMessage(`${row.customerName}: ${result.message}`);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError ? caught.message : "That could not be sent.",
      );
    } finally {
      setBusyId(null);
    }
  }

  async function resend(row: ConsultationRow) {
    if (
      !window.confirm(
        `Send the booking confirmation to ${row.customerEmail} again?`,
      )
    )
      return;

    setBusyId(row.bookingId);
    setError(null);
    try {
      const result = await api.admin.resendConfirmation(token, row.bookingId);
      setMessage(`${row.customerName}: ${result.message}`);
      if (openId === row.bookingId) {
        setDetail(await api.admin.getConsultation(token, row.bookingId));
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError ? caught.message : "That could not be sent.",
      );
    } finally {
      setBusyId(null);
    }
  }

  /**
   * The same run the scheduler makes. Here because nothing should depend on the
   * cron having been set up, and because a button that exercises the real path
   * is the only honest way to check it works.
   */
  async function runReminders() {
    if (
      !window.confirm(
        "Send tomorrow’s appointment reminders now?\n\n" +
          "Anyone who has already had one for their appointment is skipped, so this " +
          "is safe to press twice.",
      )
    ) {
      return;
    }

    setRunningReminders(true);
    setError(null);
    try {
      const result = await api.admin.runReminders(token);
      setMessage(
        result.considered === 0
          ? "Nobody is due a reminder - everyone coming up has already had theirs."
          : `${result.sent} reminder${result.sent === 1 ? "" : "s"} sent` +
              (result.skipped > 0 ? `, ${result.skipped} skipped` : "") +
              (result.failed > 0 ? `, ${result.failed} failed` : "") +
              ".",
      );
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The reminder run failed.",
      );
    } finally {
      setRunningReminders(false);
    }
  }

  const rows = (list?.bookings ?? []).filter((row) => {
    if (filter === "awaiting") return !row.submitted;
    if (filter === "received") return row.submitted;
    return true;
  });

  return (
    <div className="stack-lg">
      <div>
        <h2 className="admin__section-title">Consultations</h2>
        <p className="field-hint">
          Every confirmed appointment in the next {horizonDays} days, and
          whether its form is in. The link in each client's confirmation email
          is the same one shown here, so it can be read out over the phone if
          they have lost the email.
        </p>
      </div>

      {list && !list.emailConfigured && (
        <p className="notice notice--error">
          No email transport is configured, so nothing is actually being sent -
          each message is written to the API log instead. Set{" "}
          <code>RESEND_API_KEY</code> and <code>EMAIL_FROM</code> on the API to
          turn it on.
        </p>
      )}

      {list && list.questionCount === 0 && (
        <p className="notice">
          The form has no questions on it yet, so there is nothing for anyone to
          fill in. Add some under <strong>The form</strong>.
        </p>
      )}

      <div className="diary__toolbar">
        <div
          className="segmented"
          role="group"
          aria-label="Which appointments to show"
        >
          {FILTERS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={`segmented__option ${filter === entry.id ? "segmented__option--active" : ""}`}
              onClick={() => setFilter(entry.id)}
            >
              {entry.label}
              {list && (
                <span className="segmented__count">
                  {entry.id === "awaiting"
                    ? list.awaiting
                    : entry.id === "received"
                      ? list.received
                      : list.total}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="row">
          <label className="visually-hidden" htmlFor="horizon">
            How far ahead to look
          </label>
          <select
            id="horizon"
            className="select select--inline"
            value={horizonDays}
            onChange={(event) => setHorizonDays(Number(event.target.value))}
          >
            <option value={7}>Next 7 days</option>
            <option value={30}>Next 30 days</option>
            <option value={60}>Next 60 days</option>
            <option value={180}>Next 6 months</option>
          </select>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={runReminders}
            disabled={runningReminders}
          >
            {runningReminders ? (
              <span className="spinner" />
            ) : (
              "Send due reminders"
            )}
          </button>
        </div>
      </div>

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      {!list && (
        <p className="booking__loading">
          <span className="spinner" /> Loading…
        </p>
      )}

      {list && rows.length === 0 && (
        <p className="notice">
          {filter === "awaiting"
            ? "Every appointment in this period has its form in. Nothing to chase."
            : filter === "received"
              ? "No forms have come in for this period yet."
              : "No confirmed appointments in this period."}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="consult-list">
          {rows.map((row) => {
            const open = openId === row.bookingId;
            const days = daysUntil(row.startUtc);
            // Close and unfilled is the case worth surfacing loudly; a form due
            // in six weeks is not yet a problem.
            const pressing = !row.submitted && days <= 3;

            return (
              <li
                key={row.bookingId}
                className={`consult-item ${row.submitted ? "consult-item--done" : ""} ${
                  pressing ? "consult-item--pressing" : ""
                }`}
              >
                <div className="consult-item__when">
                  <strong>{formatShortDate(row.startUtc)}</strong>
                  <span>{formatTime(row.startUtc)}</span>
                  <span className="consult-item__countdown">
                    {days <= 0
                      ? "today"
                      : days === 1
                        ? "tomorrow"
                        : `in ${days} days`}
                  </span>
                </div>

                <div className="consult-item__who">
                  <h3 className="diary-item__name">{row.customerName}</h3>
                  <p className="diary-item__service">{row.serviceName}</p>
                  <p className="diary-item__contact">
                    {row.contactable ? (
                      <a href={`mailto:${row.customerEmail}`}>
                        {row.customerEmail}
                      </a>
                    ) : (
                      <span className="consult-item__noemail">
                        no email address
                      </span>
                    )}
                    {row.customerPhone && (
                      <>
                        <span aria-hidden="true"> · </span>
                        <a href={`tel:${row.customerPhone}`}>
                          {row.customerPhone}
                        </a>
                      </>
                    )}
                  </p>
                </div>

                <div className="consult-item__state">
                  {row.submitted ? (
                    <>
                      <span className="pill pill--ok">Form in</span>
                      <span className="consult-item__meta">
                        {row.answerCount} answer
                        {row.answerCount === 1 ? "" : "s"},{" "}
                        {ago(row.submittedUtc!)}
                        {row.updatedUtc && " · edited since"}
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        className={`pill pill--${pressing ? "dead" : "pending"}`}
                      >
                        Not in yet
                      </span>
                      <span className="consult-item__meta">
                        {row.nudgeCount === 0
                          ? "never chased"
                          : `chased ${row.nudgeCount === 1 ? "once" : `${row.nudgeCount} times`}, last ${ago(row.lastNudgeUtc!)}`}
                      </span>
                    </>
                  )}
                </div>

                <div className="consult-item__actions">
                  {!row.submitted && (
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={busyId === row.bookingId || !row.contactable}
                      title={
                        row.contactable
                          ? undefined
                          : "There is no email address on this booking, so it will have to be a phone call."
                      }
                      onClick={() => nudge(row)}
                    >
                      {busyId === row.bookingId ? (
                        <span className="spinner" />
                      ) : (
                        "Send a nudge"
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--quiet"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : row.bookingId)}
                  >
                    {open
                      ? "Close"
                      : row.submitted
                        ? "Read answers"
                        : "Details"}
                  </button>
                </div>

                {open && (
                  <div className="consult-item__detail">
                    {!detail && (
                      <p className="booking__loading">
                        <span className="spinner" /> Loading…
                      </p>
                    )}

                    {detail && (
                      <>
                        {detail.submission ? (
                          <>
                            <p className="label">
                              {detail.submission.formTitle} · sent{" "}
                              {ago(detail.submission.submittedUtc)}
                              {detail.submission.updatedUtc &&
                                ` · last edited ${ago(detail.submission.updatedUtc)}`}
                            </p>
                            <dl className="answer-list consult-answers">
                              {detail.submission.answers.map(
                                (answer, index) => (
                                  <div
                                    key={`${answer.label}-${index}`}
                                    className={
                                      answer.kind === "consent"
                                        ? "answer-list__consent"
                                        : undefined
                                    }
                                  >
                                    <dt>
                                      {answer.label}
                                      {!answer.live && (
                                        <span className="consult-answers__retired">
                                          no longer asked
                                        </span>
                                      )}
                                    </dt>
                                    <dd>
                                      {answer.kind === "consent" ? (
                                        <span className="answer-list__agreed">
                                          Agreed
                                        </span>
                                      ) : (
                                        answer.values.map((value) => (
                                          <span
                                            key={value}
                                            className="cform__answer-line"
                                          >
                                            {formatAnswer(answer.kind, value)}
                                          </span>
                                        ))
                                      )}
                                    </dd>
                                  </div>
                                ),
                              )}
                            </dl>
                          </>
                        ) : (
                          <p className="notice">
                            Nothing filled in yet. Their link is below if they
                            need it again.
                          </p>
                        )}

                        {detail.booking.notes && (
                          <p className="diary-item__notes">
                            <span className="label">Booking note</span>{" "}
                            {detail.booking.notes}
                          </p>
                        )}

                        <div className="field">
                          <label htmlFor={`link-${row.bookingId}`}>
                            Their form link
                          </label>
                          <input
                            id={`link-${row.bookingId}`}
                            className="input consult-link"
                            readOnly
                            value={detail.consultationUrl}
                            onFocus={(event) => event.currentTarget.select()}
                          />
                        </div>

                        <div className="consult-emails">
                          <p className="label">Emails</p>
                          {detail.emails.length === 0 ? (
                            <p className="field-hint">
                              Nothing has been sent to them yet.
                            </p>
                          ) : (
                            <ul className="consult-emails__list">
                              {detail.emails.map((email) => (
                                <li key={email.id}>
                                  <span
                                    className={`pill pill--${
                                      email.status === "sent"
                                        ? "ok"
                                        : email.status === "failed"
                                          ? "dead"
                                          : "pending"
                                    }`}
                                  >
                                    {email.status}
                                  </span>
                                  <span>
                                    {EMAIL_LABEL[email.kind] ?? email.kind}
                                  </span>
                                  <span className="consult-item__meta">
                                    {ago(email.createdUtc)}
                                  </span>
                                  {email.error && (
                                    <span className="consult-emails__error">
                                      {email.error}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div className="row">
                          <button
                            type="button"
                            className="btn btn--quiet"
                            disabled={
                              busyId === row.bookingId || !row.contactable
                            }
                            onClick={() => resend(row)}
                          >
                            Resend confirmation
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
